import { fetchClient } from '../base/index.js';
import { convertToFormData, replacePathParams, stringifyValues } from '../utils/index.js';
const createQueryKeyFn = (endpointDefinition) => ({ pathParameters, queryParameters, }) => [
    ...replacePathParams(endpointDefinition.path, pathParameters).split('/'),
    queryParameters,
].filter(Boolean);
const createCallableFn = (endpointDefinition, defaultFetchClient) => async (input, options) => {
    var _a;
    const bodyObject = endpointDefinition.convertToFormData
        ? {
            body: convertToFormData(input),
        }
        : input instanceof File ||
            input instanceof FormData ||
            input instanceof Blob ||
            input instanceof URLSearchParams ||
            input instanceof ArrayBuffer ||
            typeof input === 'string'
            ? { body: input }
            : { json: input };
    return ((_a = options === null || options === void 0 ? void 0 : options.fetchClient) !== null && _a !== void 0 ? _a : defaultFetchClient)
        .request(endpointDefinition.method)(replacePathParams(endpointDefinition.path, options === null || options === void 0 ? void 0 : options.pathParameters) +
        ((options === null || options === void 0 ? void 0 : options.queryParameters)
            ? '?' + new URLSearchParams(stringifyValues(options === null || options === void 0 ? void 0 : options.queryParameters)).toString()
            : ''), {
        ...bodyObject,
        headers: options === null || options === void 0 ? void 0 : options.headers,
        signal: options === null || options === void 0 ? void 0 : options.signal,
    })
        .then(async (result) => {
        const parsed = await result.json();
        return result.ok
            ? {
                data: parsed,
            }
            : { error: parsed };
    });
};
export const typedPayloadEndpoint = (endpointDefinition, defaultFetchClient = fetchClient()) => {
    const callable = createCallableFn(endpointDefinition, defaultFetchClient);
    callable.queryKey = createQueryKeyFn(endpointDefinition);
    return callable;
};
export const typedNoPayloadEndpoint = (endpointDefinition, defaultFetchClient = fetchClient()) => {
    const callable = (options) => createCallableFn(endpointDefinition, defaultFetchClient)(undefined, options);
    callable.queryKey = createQueryKeyFn(endpointDefinition);
    return callable;
};
const isFormDataLike = (input) => typeof FormData !== 'undefined' && input instanceof FormData;
const isJsonBody = (input) => input !== null &&
    input !== undefined &&
    !(typeof File !== 'undefined' && input instanceof File) &&
    !isFormDataLike(input) &&
    !(typeof Blob !== 'undefined' && input instanceof Blob) &&
    !(input instanceof URLSearchParams) &&
    !(input instanceof ArrayBuffer) &&
    typeof input !== 'string';
/**
 * Fire an `XMLHttpRequest`. Used by {@link typedPayloadProgressObserverEndpoint}
 * because `fetch` does not expose request-body upload progress in browsers or
 * React Native today, while `XMLHttpRequest.upload.onprogress` does.
 *
 * The shape of the resolved promise mirrors the typed endpoints
 * (`{ data } | { error }`) so callers can treat both interchangeably.
 */
export const xhrRequest = ({ url, method, body, headers, signal, onUploadProgress, }) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    if (headers) {
        for (const [k, v] of Object.entries(headers)) {
            try {
                xhr.setRequestHeader(k, v);
            }
            catch {
                // Some headers (e.g. Content-Type for multipart) are forbidden and
                // must be set by the browser; silently ignore.
            }
        }
    }
    const startedAt = Date.now();
    if (onUploadProgress && xhr.upload) {
        xhr.upload.onprogress = (e) => {
            const total = e.lengthComputable ? e.total : 0;
            const loaded = e.loaded;
            const percent = total > 0 ? (loaded / total) * 100 : 0;
            const elapsed = Date.now() - startedAt;
            // Skip the early frames where the rate estimate is dominated by
            // connection setup and TLS noise, otherwise the displayed ETA jumps
            // around wildly for the first second of the upload.
            const estimatedRemainingMs = total > 0 && loaded > 0 && elapsed >= 250 && percent >= 1
                ? Math.max(0, Math.round((elapsed / loaded) * (total - loaded)))
                : undefined;
            onUploadProgress({ loaded, total, percent, estimatedRemainingMs });
        };
    }
    xhr.onload = () => {
        let parsed;
        try {
            parsed = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
        }
        catch {
            parsed = undefined;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
            if (onUploadProgress) {
                onUploadProgress({
                    loaded: 1,
                    total: 1,
                    percent: 100,
                    estimatedRemainingMs: 0,
                });
            }
            resolve({ data: parsed });
        }
        else {
            resolve({ error: parsed });
        }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Request timed out'));
    if (signal) {
        if (signal.aborted) {
            xhr.abort();
            const err = new Error('Aborted');
            err.name = 'AbortError';
            reject(err);
            return;
        }
        signal.addEventListener('abort', () => {
            xhr.abort();
            const err = new Error('Aborted');
            err.name = 'AbortError';
            reject(err);
        });
    }
    xhr.send(body);
});
/**
 * Sibling of {@link typedPayloadEndpoint} that routes the request through
 * {@link xhrRequest} so callers can subscribe to upload progress events via
 * `options.onUploadProgress`. Resolves baseUrl + auth headers via the
 * `FetchClient`'s {@link ConfigAwareClient.getConfig} getter, so it shares
 * configuration with the regular fetch path.
 */
export const typedPayloadProgressObserverEndpoint = (endpointDefinition, defaultFetchClient = fetchClient()) => {
    const callable = async (input, options) => {
        var _a, _b, _c, _d;
        const path = replacePathParams(endpointDefinition.path, options === null || options === void 0 ? void 0 : options.pathParameters) +
            ((options === null || options === void 0 ? void 0 : options.queryParameters)
                ? '?' +
                    new URLSearchParams(stringifyValues(options === null || options === void 0 ? void 0 : options.queryParameters)).toString()
                : '');
        const client = (_a = options === null || options === void 0 ? void 0 : options.fetchClient) !== null && _a !== void 0 ? _a : defaultFetchClient;
        const config = await client.getConfig();
        const url = `${(_b = config.baseUrl) !== null && _b !== void 0 ? _b : ''}${path}`;
        let body;
        const extraHeaders = {};
        if (endpointDefinition.convertToFormData) {
            body = convertToFormData(input);
        }
        else if (isJsonBody(input)) {
            body = JSON.stringify(input);
            extraHeaders['Content-Type'] = 'application/json';
            extraHeaders['Accept'] = 'application/json';
        }
        else {
            body = input;
        }
        const headers = {
            ...((_c = config.headers) !== null && _c !== void 0 ? _c : {}),
            ...extraHeaders,
            ...((_d = options === null || options === void 0 ? void 0 : options.headers) !== null && _d !== void 0 ? _d : {}),
        };
        return xhrRequest({
            url,
            method: endpointDefinition.method.toUpperCase(),
            body,
            headers,
            signal: options === null || options === void 0 ? void 0 : options.signal,
            onUploadProgress: options === null || options === void 0 ? void 0 : options.onUploadProgress,
        });
    };
    callable.queryKey = createQueryKeyFn(endpointDefinition);
    return callable;
};
